import { Message } from '@bedrock/components';
import { fetchRechargeDraft } from '../rechargeService';
import RechargeForm from './RechargeFormView';
import type { RechargeEntryEnum } from '#services/api';

/**
 * 充值发起命令式助手（方案A：先调草稿校验，返回才打开抽屉）。
 * <p>
 * 对齐退款 {@code FormDrawer} 的「调用方先校验再 show」模式（QuoteInfoTable.tsx:462）。
 * 由于 withFunctionCall.show() 是同步渲染、不返回 Promise，无法内建 async 校验 + loading，
 * 故由本助手把「await fetchRechargeDraft → 成功才 show / 失败 Message.error」收口成一个 async 函数，
 * 调用方 {@code await} 它即可驱动按钮 loading。
 * <p>
 * show 时把草稿结果 {@code detail} 透传给 Drawer，组件内部 useRechargeDraft 据此跳过网络
 * 仅做预填/calc 映射 —— 草稿只调一次，避免「调用方校验 + 组件内拉草稿」调两次。
 */

export interface ShowRechargeFormConfig {
  l2_demand_id: number;
  l3_demand_id?: number;
  /** 发起入口（QUOTE_INFO / BUDGET_DASHBOARD / SUPPLIER） */
  entry: RechargeEntryEnum;
  /** 外包端（供应商）标记，草稿接口走外包路径 + 表单精简 */
  isExternal?: boolean;
  /** 海外标记（供应商端入口上下文传入） */
  overseas?: boolean;
  /** 提交成功回调（含手动关闭抽屉） */
  onSuccess?: () => void;
}

/**
 * 先调充值草稿校验 → 成功才命令式打开发起抽屉；失败 Message.error 不打开。
 * 返回 Promise，调用方 await 驱动按钮 loading。
 */
export async function showRechargeFormWithDraft(
  config: ShowRechargeFormConfig
): Promise<void> {
  const res = await fetchRechargeDraft(
    {
      l2_demand_id: config.l2_demand_id,
      l3_demand_id: config.l3_demand_id,
      entry: config.entry,
    },
    { alertError: false }
  );
  if (res?.code === 1 && res.data) {
    const inst = RechargeForm.show({
      ...config,
      detail: res.data,
      onOk: () => {
        // 提交成功回调：先执行调用方刷新，再关闭抽屉（withFunctionCall 的 onOk=destroy 会被本覆盖）
        config.onSuccess?.();
        inst.close();
      },
    });
  } else {
    Message.error(res?.message || '当前单据不可发起充值');
  }
}
